import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button';
import FilterIcon from '@atlaskit/icon/core/filter';
import GroupIcon from '@atlaskit/icon-lab/core/group';
import { Inline, Box } from '@atlaskit/primitives';
import { xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { messages } from '@databases/api-views/messages';
import { getNumberOfFiltersInGroup } from '@databases/filters/utils/getNumberOfFiltersInGroup';
import { SortIcon, HideIcon } from '@databases/icons';
import usePanel from '@databases/panel/usePanel';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { type DatabaseField, type DatabaseFieldType } from '@databases/types/database';
import {
  DatabaseLayoutType,
  type DatabaseFilterBooleanExpression,
} from '@databases/types/database/view';
import { getHiddenFields } from '@databases/utils/database/view/view';
import { getLayoutIcons } from '@databases/view-settings/utils';
import { ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import { useViewUiState } from '@databases/views/useViewUiState';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags';

const listStyles = xcss({
  listStyle: 'none',
  margin: 'space.0',
  padding: 'space.0',
});

const listContainerStyles = xcss({
  paddingBottom: 'space.100',
  paddingLeft: 'space.050',
  marginLeft: 'space.050',
});

const listItemStyles = xcss({
  margin: 'space.0',
  padding: 'space.0',
});

const badgeStyle = {
  backgroundColor: token('color.background.accent.blue.subtlest.hovered', '#CCE0FF'),
  color: token('color.text', '#172B4D'),
};

export interface ViewSettingsButtonsProps {
  hasNoFields: boolean;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
  groupField?: DatabaseField<DatabaseFieldType>;
  subgroupField?: DatabaseField<DatabaseFieldType>;
}

const ViewSettingsButtons: React.FC<ViewSettingsButtonsProps> = ({
  hasNoFields,
  openViewSettingsPanel,
  groupField,
  subgroupField,
}) => {
  const { formatMessage } = useIntl();

  const { view } = useView();
  const { activePanelSetting } = useViewUiState();
  const { isPanelVisible, hidePanel } = usePanel();

  const layoutIcons = getLayoutIcons({
    type: view.settings.layout.type,
    iconSize: 'medium',
  });

  const isViewSettingActiveInPanel = useCallback(
    (viewSetting: ViewPanelSettingType) => {
      return isPanelVisible('ViewSettingsPanel') && activePanelSetting === viewSetting;
    },
    [activePanelSetting, isPanelVisible]
  );

  const handleButtonClick = useCallback(
    (viewSetting: ViewPanelSettingType) => {
      if (isViewSettingActiveInPanel(viewSetting)) {
        hidePanel();
      } else {
        openViewSettingsPanel(viewSetting ?? null);
      }
    },
    [isViewSettingActiveInPanel, openViewSettingsPanel, hidePanel]
  );

  const hasSort = view.settings.sorts.length > 0;

  const filters = view.settings.filter as DatabaseFilterBooleanExpression | undefined;
  const numberOfFiltersInView = filters ? getNumberOfFiltersInGroup(filters) : 0;
  const showViewSettingsButtons = isPluginEnabled(PluginKey.VIEW_SETTINGS_BUTTONS);
  if (!showViewSettingsButtons) {
    return null;
  }

  const showGroupButton =
    view.settings.layout.type === DatabaseLayoutType.BOARD &&
    getExperimentValue('databasesBoardGrouping', 'enabled', {
      fireExperimentExposure: true,
    });

  return (
    <Box xcss={listContainerStyles}>
      <Inline as="ul" role="list" space="space.100" shouldWrap xcss={listStyles}>
        <Box as="li" xcss={listItemStyles}>
          <Button
            spacing="compact"
            appearance="subtle"
            isSelected={isViewSettingActiveInPanel(ViewPanelSettingType.LAYOUT)}
            iconBefore={layoutIcons.icon}
            onClick={() => handleButtonClick(ViewPanelSettingType.LAYOUT)}
            id={'view-settings-layout-button'}
            testId={'view-settings-layout-button'}
            aria-current={
              isViewSettingActiveInPanel(ViewPanelSettingType.LAYOUT) ? 'true' : undefined
            }
          >
            {formatMessage(messages['view-settings.layout.layout-text'], {
              layoutType: formatMessage(layoutIcons.label),
            })}
          </Button>
        </Box>
        <Box as="li" xcss={listItemStyles}>
          <Button
            spacing="compact"
            appearance="subtle"
            isSelected={
              isViewSettingActiveInPanel(ViewPanelSettingType.FILTER) ||
              (!!filters && numberOfFiltersInView > 0)
            }
            iconBefore={<FilterIcon label="" />}
            onClick={() => handleButtonClick(ViewPanelSettingType.FILTER)}
            id={'view-settings-filter-button'}
            testId={'view-settings-filter-button'}
            aria-current={
              isViewSettingActiveInPanel(ViewPanelSettingType.FILTER) ? 'true' : undefined
            }
          >
            <Inline alignBlock="center" space="space.050">
              {formatMessage(messages['view-settings.filter.filter-text'])}
              {filters && numberOfFiltersInView > 0 && (
                <Badge style={badgeStyle} testId="filters-badge">
                  {numberOfFiltersInView}
                </Badge>
              )}
            </Inline>
          </Button>
        </Box>
        {showGroupButton && (
          <Box as="li" xcss={listItemStyles}>
            <Button
              spacing="compact"
              appearance="subtle"
              isSelected={
                Boolean(groupField) ||
                isViewSettingActiveInPanel(ViewPanelSettingType.GROUP)
              }
              iconBefore={<GroupIcon label="" />}
              onClick={() => handleButtonClick(ViewPanelSettingType.GROUP)}
              testId={'view-settings-group-button'}
              id={'view-settings-group-button'}
              aria-current={
                isViewSettingActiveInPanel(ViewPanelSettingType.GROUP)
                  ? 'true'
                  : undefined
              }
            >
              <Inline alignBlock="center" space="space.050">
                {formatMessage(messages['view-settings.group.group-text'])}
                {groupField && (
                  <Badge style={badgeStyle} testId="groups-badge">
                    {subgroupField ? 2 : 1}
                  </Badge>
                )}
              </Inline>
            </Button>
          </Box>
        )}
        <Box as="li" xcss={listItemStyles}>
          <Button
            spacing="compact"
            appearance="subtle"
            isSelected={isViewSettingActiveInPanel(ViewPanelSettingType.SORT) || hasSort}
            isDisabled={hasNoFields}
            iconBefore={<SortIcon label="" spacing="none" />}
            onClick={() => handleButtonClick(ViewPanelSettingType.SORT)}
            testId={'view-settings-sort-button'}
            id={'view-settings-sort-button'}
            aria-current={
              isViewSettingActiveInPanel(ViewPanelSettingType.SORT) ? 'true' : undefined
            }
          >
            <Inline alignBlock="center" space="space.050">
              {formatMessage(messages['view-settings.sort.sort-text'])}
              {hasSort && (
                <Badge style={badgeStyle} testId="sorts-badge">
                  {view.settings.sorts.length}
                </Badge>
              )}
            </Inline>
          </Button>
        </Box>
        <Box as="li" xcss={listItemStyles}>
          <Button
            spacing="compact"
            appearance="subtle"
            iconBefore={<HideIcon label="" spacing="none" />}
            isSelected={
              isViewSettingActiveInPanel(ViewPanelSettingType.FIELDS) ||
              !!view.settings.fields
            }
            isDisabled={hasNoFields}
            onClick={() => handleButtonClick(ViewPanelSettingType.FIELDS)}
            testId={'view-settings-hide-field-button'}
            id={'view-settings-hide-field-button'}
            aria-current={
              isViewSettingActiveInPanel(ViewPanelSettingType.FIELDS) ? 'true' : undefined
            }
          >
            <Inline alignBlock="center" space="space.050">
              {formatMessage(messages['view-settings.hide-field.field-text'])}
              {view.settings.fields && (
                <Badge style={badgeStyle} testId="fields-badge">
                  {getHiddenFields(view.settings).length}
                </Badge>
              )}
            </Inline>
          </Button>
        </Box>
      </Inline>
    </Box>
  );
};

export default ViewSettingsButtons;
